import { PayloadAction, createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import * as exerciseService from '../../../service/exercise.service'
import { Exercise, ExerciseRequest, ExerciseSearchCriteria } from "../../../service/model/workout.model";
import { showNotification } from "../../alerts/alert.slice";
import { NotificationType } from "../../../shared/enums";
import { getAxiosErrorMessage } from "../../../service/axios/axios.call.service";

export interface ExerciseState {
    isLoading: boolean;
}

const exercisesAdapter = createEntityAdapter({
    selectId: (exercise: Exercise) => exercise.id,
    sortComparer: (a, b) => a.name?.localeCompare(b.name),
  })

export const initialState = exercisesAdapter.getInitialState({
    isLoading: false,
} as ExerciseState);

export const fetchExercises = createAsyncThunk<Exercise[], ExerciseSearchCriteria>(
    'exercises/all',
    async (searchCriteria, {rejectWithValue}) => {
        return exerciseService.getExercises(searchCriteria.searchTerm, searchCriteria.muscleGroup).catch((errors) => {
            return rejectWithValue(errors);
        })
    }
);

export const createExercise = createAsyncThunk<
    Exercise,
    ExerciseRequest
>('exercises/create', async (request, { rejectWithValue, dispatch }) => {
    return exerciseService
        .createExercise(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Exercise created successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchExercises({
                    searchTerm: '',
                    muscleGroup: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const updateExercise = createAsyncThunk<
    Exercise,
    ExerciseRequest
>('exercises/update', async (request, { rejectWithValue, dispatch }) => {
    return exerciseService
        .updateExercise(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Exercise updated successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchExercises({
                    searchTerm: '',
                    muscleGroup: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

export const deleteExercise = createAsyncThunk<
    number,
    number
>('exercises/delete', async (id, { rejectWithValue, dispatch }) => {
    return exerciseService
        .deleteExercise(id)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Exercise deleted successfully',
                    type: NotificationType.Success,
                })
            );
            dispatch(
                fetchExercises({
                    searchTerm: '',
                    muscleGroup: '',
                })
            );
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                })
            );
            return rejectWithValue(errors);
        });
});

const exerciseSlice = createSlice({
    name: 'exercises',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchExercises.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(fetchExercises.fulfilled, (state, action: PayloadAction<Exercise[]>) => {
            exercisesAdapter.setAll(state, action.payload);
            state.isLoading = false;
        })
        .addCase(fetchExercises.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(createExercise.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(createExercise.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(createExercise.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(updateExercise.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(updateExercise.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(updateExercise.rejected, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteExercise.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteExercise.fulfilled, (state) => {
            state.isLoading = false;
        })
        .addCase(deleteExercise.rejected, (state) => {
            state.isLoading = false;
        });
    }
});

export const {
    selectAll: selectExercises,
    selectById: selectExerciseById
} = exercisesAdapter.getSelectors<RootState>((state) => state.exercises);

export const selectIsLoading = (state: RootState) => state.exercises.isLoading;

export default exerciseSlice.reducer;